// packages/chat-domain/src/systemNotice.ts
//
// System messages (`senderType === 'system'`) carry short notices like "an agent
// joined". Rather than persist a notice in one fixed language, we store a tiny
// machine-readable token in the message `body` and let the UI render it (a name
// chip, localized text, …). The encoding is scoped to system messages only — a
// normal user message is never parsed this way, so the `body`-is-prose contract
// holds for user content.
//
// `agent_joined` carries the joining agent's id (so the UI can colour "me" vs
// "another agent") and display name: `agent_joined:<agentUserId>:<name>`. A
// legacy `agent_joined:<name>` (no id) is still parsed, with a null id.
//
// `ai_handoff` carries the bot→human handoff summary as already-rendered text
// (the structured summary is generated server-side; only the rendered prose
// crosses into the browser-safe core): `ai_handoff:<text>`. The text may
// contain anything (colons, newlines) — everything after the prefix is the body.

export type ChatSystemNotice =
  | { kind: 'agent_joined'; agentUserId: number | null; name: string }
  | { kind: 'ai_handoff'; text: string }
  | { kind: 'text'; text: string };

const AGENT_JOINED_PREFIX = 'agent_joined:';
const AI_HANDOFF_PREFIX = 'ai_handoff:';

/** Encode an AI→human handoff system-message body. `text` is the rendered
 *  summary the human agent reads when picking up the thread. */
export function encodeAiHandoffNotice(text: string): string {
  return `${AI_HANDOFF_PREFIX}${text}`;
}

/** Encode an agent-joined system-message body. `name` is the joining agent's
 *  (or business rep's) display name; `agentUserId` lets the viewer tell whether
 *  it was them. */
export function encodeAgentJoinedNotice(agentUserId: number, name: string): string {
  return `${AGENT_JOINED_PREFIX}${agentUserId}:${name}`;
}

/** Parse a system message body into a typed notice. */
export function parseSystemNotice(body: string): ChatSystemNotice {
  if (body.startsWith(AI_HANDOFF_PREFIX)) {
    return { kind: 'ai_handoff', text: body.slice(AI_HANDOFF_PREFIX.length) };
  }
  if (body.startsWith(AGENT_JOINED_PREFIX)) {
    const rest = body.slice(AGENT_JOINED_PREFIX.length);
    const colon = rest.indexOf(':');
    if (colon > 0) {
      const maybeId = Number(rest.slice(0, colon));
      if (Number.isInteger(maybeId) && maybeId > 0) {
        return { kind: 'agent_joined', agentUserId: maybeId, name: rest.slice(colon + 1) };
      }
    }
    // Legacy form without an id.
    return { kind: 'agent_joined', agentUserId: null, name: rest };
  }
  return { kind: 'text', text: body };
}
